/**
 * FERP Portal - useAnnotations Hook (DRR-062)
 *
 * Single hook that wraps all annotation Apollo operations so any entity
 * detail page can embed annotations with one call:
 *
 *   const annotations = useAnnotations('Task', taskId);
 *   return <AnnotationPanel {...annotations} />;
 *
 * FERP CONSTRAINTS:
 * - Repository pattern: all data via generated Apollo hooks
 * - Backend IDs only (Snowflake via createAnnotation mutation)
 * - Multi-tenant isolation via AuthCtx (tenant from auth headers)
 * - Append-only: archive with reason, no hard delete
 */

// eslint-disable-next-line no-restricted-imports -- DATA-LAYER hook
import { useQuery, useMutation, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useState, useCallback, useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/auth';
import type { AnnotationEntity } from '@/normalizers/annotations';
import type { CreateAnnotationInput } from '@/components/AnnotationPanel/AnnotationPanel';

// =============================================================================
// GraphQL Documents
//
// NOTE: These are inline because codegen has not yet run against the new backend
// schema. Once the backend is running and codegen generates typed hooks, replace
// these with imports from @/generated/graphql.
// =============================================================================

const LIST_ANNOTATIONS = gql`
  query ListAnnotations(
    $entityType: String!
    $entityId: String!
    $limit: Int
    $cursor: String
  ) {
    listAnnotations(
      entityType: $entityType
      entityId: $entityId
      limit: $limit
      cursor: $cursor
    ) {
      items {
        id
        tenant
        entityType
        entityId
        parentId
        body
        mentions
        visibility
        annotationType
        threadRootId
        replyCount
        depth
        workflowInstanceId
        safetyLevel
        relatedEntityIds
        createdBy
        createdAt
        updatedBy
        updatedAt
        version
        isActive
        archived
        archivedReason
      }
      total
      nextCursor
      hasMore
    }
  }
`;

const LIST_ANNOTATION_THREAD = gql`
  query ListAnnotationThread($threadRootId: String!, $limit: Int) {
    listAnnotationThread(threadRootId: $threadRootId, limit: $limit) {
      items {
        id
        tenant
        entityType
        entityId
        parentId
        body
        mentions
        visibility
        annotationType
        threadRootId
        replyCount
        depth
        workflowInstanceId
        safetyLevel
        relatedEntityIds
        createdBy
        createdAt
        updatedBy
        updatedAt
        version
        isActive
        archived
        archivedReason
      }
      total
      nextCursor
      hasMore
    }
  }
`;

const CREATE_ANNOTATION = gql`
  mutation CreateAnnotation($input: GqlCreateAnnotationInput!) {
    createAnnotation(input: $input) {
      id
      tenant
      entityType
      entityId
      parentId
      body
      mentions
      visibility
      annotationType
      threadRootId
      replyCount
      depth
      workflowInstanceId
      safetyLevel
      relatedEntityIds
      createdBy
      createdAt
      updatedBy
      updatedAt
      version
      isActive
      archived
      archivedReason
    }
  }
`;

const ARCHIVE_ANNOTATION = gql`
  mutation ArchiveAnnotation($annotationId: String!, $reason: String!) {
    archiveAnnotation(annotationId: $annotationId, reason: $reason) {
      success
      archivedAt
      archivedBy
      reason
      message
      annotationId
    }
  }
`;

const ANNOTATION_CREATED_SUBSCRIPTION = gql`
  subscription AnnotationCreated($entityType: String!, $entityId: String!) {
    annotationCreated(entityType: $entityType, entityId: $entityId) {
      annotationId
      entityType
      entityId
      createdBy
      body
      annotationType
      createdAt
    }
  }
`;

// =============================================================================
// Hook Options
// =============================================================================

interface UseAnnotationsOptions {
  /** Page size for list queries (default: 50) */
  limit?: number;
  /** Skip the query (e.g., if entityId not yet available) */
  skip?: boolean;
  /** Enable real-time subscription for new annotations */
  enableRealTime?: boolean;
}

// =============================================================================
// Hook Return Type
// =============================================================================

export interface UseAnnotationsReturn {
  /** Annotation data — pass directly to AnnotationPanel props */
  annotations: AnnotationEntity[];
  /** Loading state */
  loading: boolean;
  /** Error message string (null if none) */
  error: string | null;
  /** Total annotation count for this entity */
  total: number;
  /** Whether more pages exist */
  hasMore: boolean;
  /** Target entity type */
  entityType: string;
  /** Target entity ID */
  entityId: string;
  /** Thread data keyed by threadRootId */
  threads: Record<string, AnnotationEntity[]>;
  /** Whether the current user can write annotations */
  canWrite: boolean;
  /** Whether the current user can archive annotations (admin) */
  canAdmin: boolean;
  /** Create a new annotation */
  onCreateAnnotation: (input: CreateAnnotationInput) => void;
  /** Archive an annotation with reason */
  onArchiveAnnotation: (annotationId: string, reason: string) => void;
  /** Load thread replies for a given root */
  onLoadThread: (threadRootId: string) => void;
  /** Load next page of annotations */
  loadMore: () => void;
  /** Refetch the annotation list */
  refetch: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Wraps all annotation Apollo operations into a single hook.
 *
 * Returns an object whose keys match AnnotationPanelProps — spread it directly:
 *
 * ```tsx
 * const annotations = useAnnotations('Task', taskId);
 * return <AnnotationPanel {...annotations} />;
 * ```
 */
export function useAnnotations(
  entityType: string,
  entityId: string,
  options: UseAnnotationsOptions = {},
): UseAnnotationsReturn {
  const { limit = 50, skip = false, enableRealTime = true } = options;
  const { user } = useAuth();

  // ---------------------------------------------------------------------------
  // Permissions (role-based: admin+ can archive, any authenticated can write)
  // ---------------------------------------------------------------------------
  const canWrite = !!user;
  const canAdmin = !!user && (
    user.roles.includes('admin') ||
    user.roles.includes('architect') ||
    user.roles.includes('superadmin')
  );

  // ---------------------------------------------------------------------------
  // List annotations query
  // ---------------------------------------------------------------------------
  const {
    data: listData,
    loading,
    error: queryError,
    refetch,
    fetchMore,
  } = useQuery<Record<string, any>>(LIST_ANNOTATIONS, {
    variables: { entityType, entityId, limit },
    skip: skip || !entityId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const annotations: AnnotationEntity[] = useMemo(
    () => (listData?.listAnnotations?.items as AnnotationEntity[]) ?? [],
    [listData],
  );

  const total: number = listData?.listAnnotations?.total ?? 0;
  const hasMore: boolean = listData?.listAnnotations?.hasMore ?? false;
  const nextCursor: string | null = listData?.listAnnotations?.nextCursor ?? null;

  // ---------------------------------------------------------------------------
  // Thread loading
  // ---------------------------------------------------------------------------
  const [threads, setThreads] = useState<Record<string, AnnotationEntity[]>>({});

  const onLoadThread = useCallback(
    (threadRootId: string) => {
      // Already loaded
      if (threads[threadRootId]) return;

      // Manual query (not a hook — we call it on demand)
      // Using the Apollo client from the query result's context isn't available,
      // so we rely on the query hook pattern used elsewhere in FERP hooks.
    },
    [threads],
  );

  // Thread query — lazy, triggered per threadRootId
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const { data: threadData } = useQuery<Record<string, any>>(LIST_ANNOTATION_THREAD, {
    variables: { threadRootId: activeThreadId ?? '', limit: 100 },
    skip: !activeThreadId || !!threads[activeThreadId ?? ''],
    fetchPolicy: 'cache-and-network',
  });

  const handleLoadThread = useCallback(
    (threadRootId: string) => {
      if (!threads[threadRootId]) {
        setActiveThreadId(threadRootId);
      }
    },
    [threads],
  );

  // ---------------------------------------------------------------------------
  // Create annotation mutation
  // ---------------------------------------------------------------------------
  const [createAnnotationMutation] = useMutation<Record<string, any>>(CREATE_ANNOTATION, {
    onError: (err: any) => {
      notifications.show({
        title: 'Failed to create annotation',
        message: err.message,
        color: 'red',
      });
    },
  });

  const handleCreateAnnotation = useCallback(
    (input: CreateAnnotationInput) => {
      createAnnotationMutation({
        variables: {
          input: {
            entityType: input.entityType,
            entityId: input.entityId,
            parentId: input.parentId ?? null,
            body: input.body,
            mentions: input.mentions ?? [],
            visibility: input.visibility,
            annotationType: input.annotationType,
            workflowInstanceId: input.workflowInstanceId ?? null,
            relatedEntityIds: input.relatedEntityIds ?? [],
          },
        },
      });
    },
    [createAnnotationMutation],
  );

  // ---------------------------------------------------------------------------
  // Archive annotation mutation
  // ---------------------------------------------------------------------------
  const [archiveAnnotationMutation] = useMutation<Record<string, any>>(ARCHIVE_ANNOTATION, {
    onCompleted: (data: any) => {
      if (data?.archiveAnnotation?.success) {
        refetch();
        notifications.show({
          title: 'Annotation archived',
          message: data.archiveAnnotation.message ?? 'Annotation has been archived.',
          color: 'blue',
        });
      }
    },
    onError: (err: any) => {
      notifications.show({
        title: 'Failed to archive annotation',
        message: err.message,
        color: 'red',
      });
    },
  });

  const handleArchiveAnnotation = useCallback(
    (annotationId: string, reason: string) => {
      archiveAnnotationMutation({
        variables: { annotationId, reason },
      });
    },
    [archiveAnnotationMutation],
  );

  // ---------------------------------------------------------------------------
  // Real-time subscription (new annotations on this entity)
  // ---------------------------------------------------------------------------
  useSubscription<Record<string, any>>(ANNOTATION_CREATED_SUBSCRIPTION, {
    variables: { entityType, entityId },
    skip: !enableRealTime || skip || !entityId,
    onData: () => {
      // Refetch the list when a new annotation arrives via subscription
      refetch();
    },
  });

  // ---------------------------------------------------------------------------
  // Load more (cursor pagination)
  // ---------------------------------------------------------------------------
  const loadMore = useCallback(() => {
    if (!hasMore || !nextCursor) return;
    fetchMore({
      variables: { cursor: nextCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          listAnnotations: {
            ...fetchMoreResult.listAnnotations,
            items: [
              ...(prev.listAnnotations?.items ?? []),
              ...(fetchMoreResult.listAnnotations?.items ?? []),
            ],
          },
        };
      },
    });
  }, [hasMore, nextCursor, fetchMore]);

  // ---------------------------------------------------------------------------
  // Return — keys match AnnotationPanelProps for direct spread
  // ---------------------------------------------------------------------------
  return {
    entityType,
    entityId,
    annotations,
    loading,
    error: queryError?.message ?? null,
    total,
    hasMore,
    threads,
    canWrite,
    canAdmin,
    onCreateAnnotation: handleCreateAnnotation,
    onArchiveAnnotation: handleArchiveAnnotation,
    onLoadThread: handleLoadThread,
    loadMore,
    refetch: () => { refetch(); },
  };
}

export default useAnnotations;
